import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { toast } from 'sonner';
import { z } from 'zod';
import Honeybadger from '@honeybadger-io/js';
import { zodResolver } from '@hookform/resolvers/zod';

import { deserializer } from '../../../../shared/utils/deserializer';
import { SUCCESS_MESSAGES } from '../constants';
import {
  createTaskStatus as apiCreateTaskStatus,
  updateTaskStatus as apiUpdateTaskStatus,
} from '../services/taskStatusService';
import useBoard from './useBoard';
import useDialogs from './useDialogs';

const formSchema = z.object({
  name: z.string().min(1, 'Status name is required.'),
});

const useStatusForm = () => {
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { activeStatus, handleCloseStatusFormDialog: handleClose } = useDialogs();
  const { addStatus, updateStatus } = useBoard();

  const defaultValues = {
    name: activeStatus?.name ?? '',
  };

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  // For repopulating :defaultValues when :activeStatus changes.
  useEffect(() => {
    form.reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStatus]);

  const createTaskStatus = async (taskStatusData) => {
    const serializedTaskStatus = await apiCreateTaskStatus(taskStatusData);
    return deserializer.deserialize(serializedTaskStatus);
  };

  const updateTaskStatus = async (taskStatusData, taskStatusId) => {
    const serializedTaskStatus = await apiUpdateTaskStatus({
      taskStatusData,
      taskStatusId,
    });

    return deserializer.deserialize(serializedTaskStatus);
  };

  const handleSuccess = (taskStatus) => {
    if (activeStatus) {
      updateStatus(taskStatus);
      toast.success(SUCCESS_MESSAGES.status.update);
    } else {
      addStatus(taskStatus);
      toast.success(SUCCESS_MESSAGES.status.create);
    }

    handleClose();
    form.reset();
  };

  const handleSubmit = async (formData) => {
    try {
      setErrorMessage('');
      setIsSubmitting(true);

      const taskStatus = activeStatus
        ? await updateTaskStatus(formData, activeStatus.id)
        : await createTaskStatus(formData);

      handleSuccess(taskStatus);
    } catch (error) {
      setErrorMessage(error.message);
      Honeybadger.notify(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (open) => {
    if (!open) {
      handleClose();
      setErrorMessage('');
      form.reset();
    }
  };

  return {
    errorMessage,
    form,
    handleOpenChange,
    handleSubmit,
    isSubmitting,
  };
};

export default useStatusForm;
